import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import { Row, Col } from "react-bootstrap";
import { ServiceQuotationFormValues } from "../serviceInitialValues";
import { MecordColors } from "Constants/Color";
import { formatIndianAmount } from "helpers/formatIndianAmount";

const TotalSummarySection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<ServiceQuotationFormValues>();

  const roundToTwoDecimals = (num: number): number => Math.round(num * 100) / 100;

  const isCompositional = values.company?.gst_type === "Compositional gst";

  // Check Intra-State vs Inter-State
  const isIntraState = (() => {
    const vals = values as any;
    const billFromState = (
      vals.bill_from_address?.org_state ||
      vals.bill_from_address?.state ||
      ""
    )
      .toString()
      .trim()
      .toLowerCase();
    const billToState = (
      vals.company?.billing_address?.state ||
      vals.billing_address?.state ||
      vals.customer_details?.billing_address?.state ||
      ""
    )
      .toString()
      .trim()
      .toLowerCase();
    return !!billFromState && !!billToState && billFromState === billToState;
  })();

  // Calculate totals on every render for immediate UI update
  const serviceTotals = values.services.reduce(
    (acc, service) => {
      const serviceAny = service as any;
      const unitPrice = parseFloat(serviceAny.unit_price) || parseFloat(serviceAny.service_amount) || parseFloat(serviceAny.serviceRate) || 0;
      const quantity = parseFloat(serviceAny.quantity) || parseFloat(serviceAny.product_quantity) || parseFloat(serviceAny.productQuantity) || 1;
      const grossValue = roundToTwoDecimals(unitPrice * quantity);
      const discountValue = roundToTwoDecimals(parseFloat(serviceAny.calculated_discount_value) || 0);
      const netValue = roundToTwoDecimals(grossValue - discountValue);
      const gstPercent = isCompositional ? 0 : (parseFloat(serviceAny.gst_percentage) || parseFloat(serviceAny.gst_percent) || 0);
      const gstValue = roundToTwoDecimals((netValue * gstPercent) / 100);

      return {
        subtotal: acc.subtotal + grossValue,
        totalDiscounts: acc.totalDiscounts + discountValue,
        totalGst: acc.totalGst + gstValue,
      };
    },
    { subtotal: 0, totalDiscounts: 0, totalGst: 0 }
  );

  const accessoryTotals = values.services.reduce(
    (acc, service) => {
      const serviceAny = service as any;
      let accSubtotal = 0;
      let accDiscount = 0;
      let accGst = 0;

      const accessories = Array.isArray(serviceAny.accessories) ? serviceAny.accessories : [];
      accessories.forEach((item: any) => {
        const accQty = Number(item.quantity) || 0;
        const accRate = Number(item.unit_price) || 0;
        const accGross = Number(item.gross_value) || (accQty * accRate);
        const accDisc = Number(item.discount_amount) || 0;
        const accTaxable = Math.max(accGross - accDisc, 0);
        const accGstVal = isCompositional ? 0 : (Number(item.gst_value) || 0);

        accSubtotal += accGross;
        accDiscount += accDisc;
        accGst += accGstVal;
      });

      return {
        subtotal: acc.subtotal + accSubtotal,
        totalDiscounts: acc.totalDiscounts + accDiscount,
        totalGst: acc.totalGst + accGst,
      };
    },
    { subtotal: 0, totalDiscounts: 0, totalGst: 0 }
  );

  const roundedSubtotal = roundToTwoDecimals(serviceTotals.subtotal);
  const roundedDiscounts = roundToTwoDecimals(serviceTotals.totalDiscounts);
  const roundedGst = roundToTwoDecimals(serviceTotals.totalGst);
  const netTotal = roundToTwoDecimals(roundedSubtotal - roundedDiscounts);
  const grandTotal = roundToTwoDecimals(netTotal + roundedGst);

  // Service GST split
  const serviceCgst = isCompositional ? 0 : isIntraState ? roundToTwoDecimals(roundedGst / 2) : 0;
  const serviceSgst = isCompositional ? 0 : isIntraState ? roundToTwoDecimals(roundedGst - serviceCgst) : 0;
  const serviceIgst = isCompositional ? 0 : !isIntraState ? roundedGst : 0;

  const roundedAccSub = roundToTwoDecimals(accessoryTotals.subtotal);
  const roundedAccDis = roundToTwoDecimals(accessoryTotals.totalDiscounts);
  const roundedAccGst = roundToTwoDecimals(accessoryTotals.totalGst);
  const accNet = roundToTwoDecimals(roundedAccSub - roundedAccDis);
  const accGrand = roundToTwoDecimals(accNet + roundedAccGst);

  // Accessory GST split
  const accCgst = isCompositional ? 0 : isIntraState ? roundToTwoDecimals(roundedAccGst / 2) : 0;
  const accSgst = isCompositional ? 0 : isIntraState ? roundToTwoDecimals(roundedAccGst - accCgst) : 0;
  const accIgst = isCompositional ? 0 : !isIntraState ? roundedAccGst : 0;

  const combinedSub = roundToTwoDecimals(roundedSubtotal + roundedAccSub);
  const combinedDis = roundToTwoDecimals(roundedDiscounts + roundedAccDis);
  const combinedNet = roundToTwoDecimals(netTotal + accNet);
  const combinedGst = roundToTwoDecimals(roundedGst + roundedAccGst);
  const combinedGrand = roundToTwoDecimals(grandTotal + accGrand);

  // Combined GST split
  const totalCgst = roundToTwoDecimals(serviceCgst + accCgst);
  const totalSgst = roundToTwoDecimals(serviceSgst + accSgst);
  const totalIgst = roundToTwoDecimals(serviceIgst + accIgst);

  // Sync to formik values in useEffect to prevent infinite loops
  useEffect(() => {
    setFieldValue("subtotal_before_discount", combinedSub, false);
    setFieldValue("total_item_discounts", combinedDis, false);
    setFieldValue("net_total", combinedNet, false);
    setFieldValue("total_gst_value", combinedGst, false);
    setFieldValue("grand_total_amount", combinedGrand, false);
  }, [combinedSub, combinedDis, combinedNet, combinedGst, combinedGrand, setFieldValue]);

  const symbol = (values as any).currency_symbol || "₹";

  return (
    <div className="">
      <h5 className="fw-semibold mb-3">Total Summary</h5>

      <div className="rounded p-3" style={{ backgroundColor: MecordColors.LightThemeBGColor }}>
        <div className="mb-2 fw-semibold" style={{ fontSize: "13px" }}>Service</div>
        <Row className="mb-2">
          <Col xs={8}>
            <span>Subtotal (Before Discount):</span>
          </Col>
          <Col xs={4} className="text-end">
            <strong>{`${symbol} ${formatIndianAmount(roundedSubtotal)}`}</strong>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col xs={8}>
            <span>Total Item Discounts:</span>
          </Col>
          <Col xs={4} className="text-end">
            <strong>{`${symbol} ${formatIndianAmount(roundedDiscounts)}`}</strong>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col xs={8}>
            <span>Net Total:</span>
          </Col>
          <Col xs={4} className="text-end">
            <strong>{`${symbol} ${formatIndianAmount(netTotal)}`}</strong>
          </Col>
        </Row>

        {!isCompositional && (
          isIntraState ? (
            <>
              <Row className="mb-2">
                <Col xs={8}>
                  <span>CGST:</span>
                </Col>
                <Col xs={4} className="text-end">
                  <strong>{`${symbol} ${formatIndianAmount(serviceCgst)}`}</strong>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={8}>
                  <span>SGST:</span>
                </Col>
                <Col xs={4} className="text-end">
                  <strong>{`${symbol} ${formatIndianAmount(serviceSgst)}`}</strong>
                </Col>
              </Row>
            </>
          ) : (
            <Row className="mb-2">
              <Col xs={8}>
                <span>IGST:</span>
              </Col>
              <Col xs={4} className="text-end">
                <strong>{`${symbol} ${formatIndianAmount(serviceIgst)}`}</strong>
              </Col>
            </Row>
          )
        )}

        <Row className="mb-2">
          <Col xs={8}>
            <span>Total GST Value:</span>
          </Col>
          <Col xs={4} className="text-end">
            <strong>{`${symbol} ${formatIndianAmount(roundedGst)}`}</strong>
          </Col>
        </Row>

        <hr />
        <div className="mb-2 fw-semibold" style={{ fontSize: "13px" }}>Accessories</div>
        <Row className="mb-2">
          <Col xs={8}>
            <span>Subtotal (Before Discount):</span>
          </Col>
          <Col xs={4} className="text-end">
            <strong>{`${symbol} ${formatIndianAmount(roundedAccSub)}`}</strong>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col xs={8}>
            <span>Total Discount Value:</span>
          </Col>
          <Col xs={4} className="text-end">
            <strong>{`${symbol} ${formatIndianAmount(roundedAccDis)}`}</strong>
          </Col>
        </Row>
        <Row className="mb-2">
          <Col xs={8}>
            <span>Net Total:</span>
          </Col>
          <Col xs={4} className="text-end">
            <strong>{`${symbol} ${formatIndianAmount(accNet)}`}</strong>
          </Col>
        </Row>

        {!isCompositional && (
          isIntraState ? (
            <>
              <Row className="mb-2">
                <Col xs={8}>
                  <span>CGST:</span>
                </Col>
                <Col xs={4} className="text-end">
                  <strong>{`${symbol} ${formatIndianAmount(accCgst)}`}</strong>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs={8}>
                  <span>SGST:</span>
                </Col>
                <Col xs={4} className="text-end">
                  <strong>{`${symbol} ${formatIndianAmount(accSgst)}`}</strong>
                </Col>
              </Row>
            </>
          ) : (
            <Row className="mb-2">
              <Col xs={8}>
                <span>IGST:</span>
              </Col>
              <Col xs={4} className="text-end">
                <strong>{`${symbol} ${formatIndianAmount(accIgst)}`}</strong>
              </Col>
            </Row>
          )
        )}

        <Row className="mb-2">
          <Col xs={8}>
            <span>Total GST Value:</span>
          </Col>
          <Col xs={4} className="text-end">
            <strong>{`${symbol} ${formatIndianAmount(roundedAccGst)}`}</strong>
          </Col>
        </Row>

        <hr />
        {!isCompositional && (
          <>
            {isIntraState ? (
              <>
                <Row className="mb-2">
                  <Col xs={8}>
                    <span>Total CGST:</span>
                  </Col>
                  <Col xs={4} className="text-end">
                    <strong>{`${symbol} ${formatIndianAmount(totalCgst)}`}</strong>
                  </Col>
                </Row>
                <Row className="mb-2">
                  <Col xs={8}>
                    <span>Total SGST:</span>
                  </Col>
                  <Col xs={4} className="text-end">
                    <strong>{`${symbol} ${formatIndianAmount(totalSgst)}`}</strong>
                  </Col>
                </Row>
              </>
            ) : (
              <Row className="mb-2">
                <Col xs={8}>
                  <span>Total IGST:</span>
                </Col>
                <Col xs={4} className="text-end">
                  <strong>{`${symbol} ${formatIndianAmount(totalIgst)}`}</strong>
                </Col>
              </Row>
            )}
          </>
        )}

        <Row>
          <Col xs={8}>
            <span className="fw-bold">Grand Amount:</span>
          </Col>
          <Col xs={4} className="text-end">
            <strong className="fw-bold" style={{ fontSize: "1.2rem" }}>
              {`${symbol} ${formatIndianAmount(combinedGrand)}`}
            </strong>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default TotalSummarySection;







